/**
 * Utility to track page reloads and detect if page hasn't reloaded for 12 hours
 * Uses localStorage to persist across page reloads
 */

const STORAGE_KEY_LAST_RELOAD = "checkin_last_reload_time";
const STORAGE_KEY_INITIAL_LOAD = "checkin_initial_load_time";
const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

/**
 * Initialize reload tracking - call this on page load
 * Returns true if this is a fresh load, false if it's a reload
 */
export const initializeReloadTracking = () => {
  const now = Date.now();
  const lastReloadTime = localStorage.getItem(STORAGE_KEY_LAST_RELOAD);
  const initialLoadTime = localStorage.getItem(STORAGE_KEY_INITIAL_LOAD);

  // If no initial load time exists, this is the first load
  if (!initialLoadTime) {
    localStorage.setItem(STORAGE_KEY_INITIAL_LOAD, now.toString());
    localStorage.setItem(STORAGE_KEY_LAST_RELOAD, now.toString());
    console.log("[ReloadTracker] First load detected, initializing tracking");
    return true; // Fresh load
  }

  // Update last reload time
  localStorage.setItem(STORAGE_KEY_LAST_RELOAD, now.toString());
  
  const timeSinceLastReload = now - parseInt(lastReloadTime, 10);
  const isReload = timeSinceLastReload > 1000; // More than 1 second = reload (not just navigation)

  console.log("[ReloadTracker] Page loaded", {
    isReload,
    timeSinceLastReload: `${Math.round(timeSinceLastReload / 1000)}s`,
    lastReloadTime: new Date(parseInt(lastReloadTime, 10)).toISOString(),
  });

  return !isReload; // Return true if fresh, false if reload
};

/**
 * Check if the page hasn't fully reloaded for 12 hours
 * @returns {Object} { hasNotReloaded: boolean, hoursSinceLastReload: number, shouldForceReload: boolean }
 */
export const checkReloadStatus = () => {
  const now = Date.now();
  const lastReloadTime = localStorage.getItem(STORAGE_KEY_LAST_RELOAD);
  const initialLoadTime = localStorage.getItem(STORAGE_KEY_INITIAL_LOAD);

  if (!lastReloadTime || !initialLoadTime) {
    // No tracking data, assume fresh
    return {
      hasNotReloaded: false,
      hoursSinceLastReload: 0,
      shouldForceReload: false,
      lastReloadTime: null,
    };
  }

  const lastReload = parseInt(lastReloadTime, 10);
  const timeSinceLastReload = now - lastReload;
  const hoursSinceLastReload = timeSinceLastReload / (60 * 60 * 1000);
  const hasNotReloaded = timeSinceLastReload >= TWELVE_HOURS_MS;
  const shouldForceReload = hasNotReloaded;

  return {
    hasNotReloaded,
    hoursSinceLastReload: Math.round(hoursSinceLastReload * 100) / 100,
    shouldForceReload,
    lastReloadTime: new Date(lastReload).toISOString(),
    initialLoadTime: new Date(parseInt(initialLoadTime, 10)).toISOString(),
  };
};

/**
 * Get detailed reload status information
 * @returns {Object} Status object with all tracking information
 */
export const getReloadStatus = () => {
  const status = checkReloadStatus();
  const now = Date.now();
  const initialLoadTime = localStorage.getItem(STORAGE_KEY_INITIAL_LOAD);
  
  let totalUptime = 0;
  if (initialLoadTime) {
    totalUptime = now - parseInt(initialLoadTime, 10);
  }

  return {
    ...status,
    totalUptimeHours: Math.round((totalUptime / (60 * 60 * 1000)) * 100) / 100,
    currentTime: new Date(now).toISOString(),
  };
};

/**
 * Force clear reload tracking (useful for testing or reset)
 */
export const clearReloadTracking = () => {
  localStorage.removeItem(STORAGE_KEY_LAST_RELOAD);
  localStorage.removeItem(STORAGE_KEY_INITIAL_LOAD);
  console.log("[ReloadTracker] Tracking cleared");
};

/**
 * Manually trigger a reload timestamp update (useful for soft reloads)
 */
export const updateReloadTime = () => {
  const now = Date.now();
  localStorage.setItem(STORAGE_KEY_LAST_RELOAD, now.toString());
  console.log("[ReloadTracker] Reload time updated manually");
};

